import React, {CSSProperties} from "react";
import {animated, useSpring} from "@react-spring/web";
import {FlexCol} from "./Containers";
import {COLORS, ColorKey} from "./BCColors";

// Font weight mapping
export const FONT_WEIGHTS = {
    lightest: 100,
    lighter: 200,
    light: 300,
    normal: 400,
    semiBold: 500,
    bold: 600,
    bolder: 700,
    boldest: 900,
} as const;

export type FontWeightKey = keyof typeof FONT_WEIGHTS;

// Shared animation configurations
export const useHeaderSpring = () => useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { mass: 1, tension: 180, friction: 22 },
    delay: 100
});

export const useContentSpring = () => useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { mass: 1, tension: 180, friction: 22 },
    delay: 500
});

// Subtle animation for content pages - title and content animate together
export const useContentPageSpring = () => useSpring({
    from: { opacity: 0, y: 15 },
    to: { opacity: 1, y: 0 },
    config: { mass: 0.8, tension: 200, friction: 25 },
    delay: 150
});

export const useWelcomeSpring = () => useSpring({
    from: { opacity: 0, y: 30 },
    to: { opacity: 1, y: 0 },
    config: { mass: 1, tension: 180, friction: 22 },
    delay: 100
});

export const useStatsSpring = () => useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { mass: 1, tension: 180, friction: 22 },
    delay: 300
});

export interface TextStylingProps {
    textAlign?: "left" | "right" | "center" | "justify";
    style?: CSSProperties;
    fontSize?: number;
    color?: ColorKey;
    letterSpacing?: number;
    fontWeight?: number | FontWeightKey;
    whiteSpace?: "normal" | "nowrap" | "pre" | "pre-wrap" | "pre-line" | "break-spaces";
    margin?: number;
    vMargin?: number;
    hMargin?: number;
    mTop?: number;
    mBottom?: number;
    mLeft?: number;
    mRight?: number;
    padding?: number;
    pTop?: number;
    pBottom?: number;
    pLeft?: number;
    pRight?: number;
    pHorizontal?: number;
    pVertical?: number;
}

interface TextProps extends TextStylingProps {
    text: string;
    className?: string;
}

export function getStyling({ 
    textAlign, 
    style,
    fontSize,
    color,
    letterSpacing,
    fontWeight,
    whiteSpace,
    margin,
    vMargin,
    hMargin,
    mTop,
    mBottom,
    mLeft,
    mRight,
    padding,
    pTop,
    pBottom,
    pLeft,
    pRight,
    pHorizontal,
    pVertical
}: TextStylingProps): CSSProperties | undefined {
    const computedStyle: CSSProperties = {
        ...(style || {}),
        ...(textAlign && { textAlign }),
        ...(fontSize && { fontSize: `${fontSize}rem` }),
        ...(color && { color: COLORS[color] }),
        ...(letterSpacing && { letterSpacing: `${letterSpacing}em` }),
        ...(fontWeight !== undefined && { 
            fontWeight: typeof fontWeight === 'string' && fontWeight in FONT_WEIGHTS 
                ? FONT_WEIGHTS[fontWeight as FontWeightKey] 
                : fontWeight 
        }),
        ...(whiteSpace && { whiteSpace }),
        // Margins
        ...(margin && { margin: `${margin}em` }),
        ...(vMargin && { marginTop: `${vMargin}em`, marginBottom: `${vMargin}em` }),
        ...(hMargin && { marginLeft: `${hMargin}em`, marginRight: `${hMargin}em` }),
        ...(mTop && { marginTop: `${mTop}em` }),
        ...(mBottom && { marginBottom: `${mBottom}em` }),
        ...(mLeft && { marginLeft: `${mLeft}em` }),
        ...(mRight && { marginRight: `${mRight}em` }),
        // Padding
        ...(padding && { padding: `${padding}em` }),
        ...(pTop && { paddingTop: `${pTop}em` }),
        ...(pBottom && { paddingBottom: `${pBottom}em` }),
        ...(pLeft && { paddingLeft: `${pLeft}em` }),
        ...(pRight && { paddingRight: `${pRight}em` }),
        ...(pHorizontal && { paddingLeft: `${pHorizontal}em`, paddingRight: `${pHorizontal}em` }),
        ...(pVertical && { paddingTop: `${pVertical}em`, paddingBottom: `${pVertical}em` }),
    };
    
    // Only return if there are actual styles to apply
    return Object.keys(computedStyle).length > 0 ? computedStyle : undefined;
}

export const BCHeader1: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h1 className={className} style={getStyling(styling)}>{text}</h1>
);

export const BCHeader2: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h2 className={className} style={getStyling(styling)}>{text}</h2>
);

export const BCHeader3: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h3 className={className} style={getStyling(styling)}>{text}</h3>
);

export const BCHeader4: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h4 className={className} style={getStyling(styling)}>{text}</h4>
);

export const BCHeader5: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h5 className={className} style={getStyling(styling)}>{text}</h5>
);

export const BCHeader6: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <h6 className={className} style={getStyling(styling)}>{text}</h6>
);

export const Subtitle1: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <p className={`subtitle-1 ${className}`} style={getStyling(styling)}>{text}</p>
);

export const Subtitle2: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <p className={`subtitle-2 ${className}`} style={getStyling(styling)}>{text}</p>
);

export const Body1: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <p className={`body-1 ${className}`} style={getStyling(styling)}>{text}</p>
);

export const Body2: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <p className={`body-2 ${className}`} style={getStyling(styling)}>{text}</p>
);

export const Body3: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <p className={`body-3 ${className}`} style={getStyling(styling)}>{text}</p>
);

export const Caption: React.FC<TextProps> = ({ text, className = "", ...styling }) => (
    <span className={`caption ${className}`} style={getStyling(styling)}>{text}</span>
);

// Header + Subheader combo
interface HeaderSubheaderProps {
    text: string;
    subtext?: string;
    secondaryColorSubheader?: boolean;
    className?: string;
}

export const HeaderSubheader: React.FC<HeaderSubheaderProps> = ({
                                                                    text,
                                                                    subtext,
                                                                    className = "",
                                                                }) => (
    <div className={`header-text-container ${className}`}>
        <BCHeader1 text={text} />
        {subtext && (
            <Subtitle1 text={subtext} />
        )}
    </div>
);

interface EnhancedPageTitleProps {
    title: string;
    subtitle?: string;
}

export const EnhancedPageTitle: React.FC<EnhancedPageTitleProps> = ({ title, subtitle }) => {
    return (
        <FlexCol 
            gap={1} 
            align="flex-start" 
            padding={1.5}
            style={{
                background: 'linear-gradient(135deg, var(--blue-background) 0%, var(--primary2) 100%)',
                borderRadius: '1em',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 6px 24px rgba(224, 238, 254, 0.3)',
                border: '1px solid var(--border-default)',
                marginBottom: '1.5em'
            }}
        >
            {/* Background Pattern */}
            <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-20%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, var(--primary-blue) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
                opacity: 0.1,
                zIndex: 0
            }} />
            
            {/* Main Content */}
            <FlexCol gap={0.5} align="flex-start" style={{ position: 'relative', zIndex: 1 }}>
                <BCHeader2 
                    text={title} 
                    className="page-title" 
                    style={{ 
                        color: 'var(--text-color)',
                        textShadow: 'none',
                        fontSize: '2em',
                        fontWeight: '700'
                    }}
                />
                {subtitle && (
                    <Subtitle1 
                        text={subtitle}
                        textAlign="left" 
                        style={{ 
                            color: 'var(--secondary-text-color)',
                            fontSize: '1em',
                            lineHeight: '1.4'
                        }}
                    />
                )}
            </FlexCol>
        </FlexCol>
    );
};